import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from '../../../components/ui/Button';
import { Branch, deleteBranch } from '../../../utils/supabaseClient';

import { useAppDispatch } from '../../../store/hooks'
import { deleteCategoryData } from '../../../store/slices/categorySlice'
import { deleteBranchData } from '../../../store/slices/branchSlice';
import { getBranchName } from '../../../utils/brandHelper';

interface DeleteBranchModalProps {
	isOpen: boolean;
	branch: Branch;
	onClose: () => void;
	onBranchDeleted: (branch?: Branch) => void;
}

const DeleteBranchModal: React.FC<DeleteBranchModalProps> = ({
	isOpen,
	branch,
	onClose,
	onBranchDeleted
}) => {
	const { t, i18n } = useTranslation();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const dispatch = useAppDispatch()

	const handleConfirm = async () => {
		setIsSubmitting(true);
		try {

			await dispatch(deleteBranchData(branch.id))
			onBranchDeleted()
		} finally {
			setIsSubmitting(false);
		}
	};

	const modalVariants = {
		hidden: { opacity: 0, scale: 0.9 },
		visible: {
			opacity: 1,
			scale: 1,
			transition: { duration: 0.2 }
		},
		exit: {
			opacity: 0,
			scale: 0.9,
			transition: { duration: 0.15 }
		}
	};

	const overlayVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
		exit: { opacity: 0 }
	};

	return (
		<>
			{isOpen && (<div className="fixed inset-0 z-50 overflow-y-auto">
				<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
					<motion.div
						variants={overlayVariants}
						initial="hidden"
						animate="visible"
						exit="exit"
						className="fixed inset-0 bg-black/50"
						onClick={onClose}
					/>

					<motion.div
						variants={modalVariants}
						initial="hidden"
						animate="visible"
						exit="exit"
						className="z-10 inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
					>
						<div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
							<div className="sm:flex sm:items-start">
								<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
									<FiAlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
								</div>
								<div className="mt-3 text-center sm:mt-0 sm:ms-4 sm:text-start">
									<h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
										{t('branches.deleteTitle')}
									</h3>
									<div className="mt-2">
										<p className="text-sm text-gray-500 dark:text-gray-400">
											{t('branches.deleteConfirmation', { name: getBranchName(branch, i18n.language) })}
										</p>
									</div>
								</div>
							</div>
						</div>
						<div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 flex flex-col sm:flex-row-reverse gap-2">
							<Button
								variant="outline"
								className="!border-red-500 !text-red-500 hover:!bg-red-50 dark:hover:!bg-red-900/20"
								onClick={handleConfirm}
								isLoading={isSubmitting}
							>
								{t('common.delete')}
							</Button>
							<Button
								variant="outline"
								onClick={onClose}
								disabled={isSubmitting}
								className="w-full sm:w-auto"
							>
								{t('common.cancel')}
							</Button>
						</div>
					</motion.div>

				</div>
			</div>)}
		</>
	);
};

export default DeleteBranchModal;